import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { App } from "antd";

import { useSurveyViewForm } from "@/features/survey-views/form/hooks/useSurveyViewForm";
import {
  useGetSurveyViewByIdQuery,
  useUpdateSurveyViewMutation,
} from "@/redux/service";
import SurveyViewForm from "@/features/survey-views/form";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { SurveyViewFormValues } from "@/features/survey-views/form/types";
import { SurveyViewColorScheme } from "@/redux/survey-views/types";
import { SharingRights } from "@/shared/utils/constants";
import { toFormAnswers } from "@/features/survey-views/form/utils";
import { handleErrors } from "@/shared/utils/forms";

const SurveyViewUpdate = () => {
  const { surveyViewUuid } = useParams();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { form } = useSurveyViewForm();
  const [updateSurveyView] = useUpdateSurveyViewMutation();
  // A cached view can still list an answer that was deleted in the meantime, so
  // the view is always loaded fresh when the form opens.
  const surveyViewResponse = useGetSurveyViewByIdQuery(
    surveyViewUuid as string,
    { refetchOnMountOrArgChange: true },
  );
  const [originalEvidenceIds, setOriginalEvidenceIds] = useState<number[]>([]);
  const [isFormLoaded, setIsFormLoaded] = useState(false);

  useEffect(() => {
    if (isFormLoaded || surveyViewResponse.isFetching) return;
    const surveyView = surveyViewResponse.data;
    if (!surveyView) return;

    const answers = toFormAnswers(surveyView.answers);

    setOriginalEvidenceIds(
      answers.flatMap((answer) =>
        answer.attachedBlocks
          .filter((block) =>
            [
              SharingRights.PRME_COMMUNITY.value,
              SharingRights.PRIVATE.value,
            ].includes(block.sharingRights),
          )
          .map((block) => block.id),
      ),
    );

    form.reset({
      id: surveyViewUuid,
      title: surveyView.title,
      description: surveyView.description,
      survey: surveyView.survey,
      config: {
        ...surveyView.config,
        grayScale:
          surveyView.config.colorScheme === SurveyViewColorScheme.GRAY_SCALE,
      },
      answers,
    });
    setIsFormLoaded(true);
  }, [
    surveyViewResponse.data,
    surveyViewResponse.isFetching,
    isFormLoaded,
    form,
    surveyViewUuid,
  ]);

  const handleSubmit = async (values: SurveyViewFormValues) => {
    if (!surveyViewUuid) return;

    const data = {
      ...values,
      config: {
        ...values.config,
        colorScheme: values.config.grayScale
          ? SurveyViewColorScheme.GRAY_SCALE
          : SurveyViewColorScheme.DEFAULT,
      },
      answers: values.answers.map((answer) => ({
        answer: answer.answer,
        attachedBlocks: answer.attachedBlocks.map((block) => block.id),
      })),
    };

    try {
      const response = await updateSurveyView({
        id: surveyViewUuid,
        data,
      }).unwrap();
      notification.success({
        message: t("surveyViews.labels.surveyViewUpdated"),
      });
      navigate(`/reports/views/${response.id}`);
      form.reset();
    } catch (error) {
      handleErrors(
        error as Parameters<typeof handleErrors>[0],
        form.setError,
        notification,
        t,
      );
    }
  };

  if (!isFormLoaded && !surveyViewResponse.isError) {
    return <BaseSpinner />;
  }

  return (
    <SurveyViewForm
      form={form}
      handleSubmit={form.handleSubmit(handleSubmit)}
      originalEvidenceIds={originalEvidenceIds}
    />
  );
};

export default SurveyViewUpdate;
